<template>
  <div>
    <!-- ✅ MODAL DE CARREGAMENTO MODERNO -->
    <v-dialog v-model="loadingDialog" persistent max-width="400">
      <v-card class="loading-card">
        <v-card-text class="text-center pa-6">
          <v-progress-circular 
            indeterminate 
            color="blue-darken-1" 
            size="48"
            width="4"
            class="mb-4"
          ></v-progress-circular>
          <div class="text-h6 font-weight-medium mb-2">Carregando...</div>
          <div class="text-body-2 text-grey">Buscando dados da máquina</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ✅ MODAL PRINCIPAL FULLSCREEN -->
    <v-dialog
      v-model="ativarMaquina"
      fullscreen
      persistent
      scrollable
      class="machine-dialog"
      transition="dialog-bottom-transition"
    >
      <v-card class="machine-details-card">
        <!-- ✅ HEADER FIXO COM NOME DA MÁQUINA -->
        <v-app-bar 
          color="blue-darken-1"
          elevation="4"
          class="machine-header"
          fixed
          app
        >
          <v-app-bar-title class="machine-title">
            <div class="d-flex align-center">
              <v-avatar size="40" color="white" variant="tonal" class="mr-3">
                <v-icon color="blue-darken-1" size="24">mdi-cog</v-icon>
              </v-avatar>
              <div class="title-content">
                <div class="machine-name text-truncate">
                  {{ maquinaData?.descricao_maquina || 'Carregando dados da máquina...' }}
                </div>
                <div class="machine-id">
                  MAQ{{ formatId(maquinaData?.id) }}
                  <span v-if="maquinaData?.numero_serie" class="ml-2">
                    • {{ maquinaData.numero_serie }}
                  </span>
                </div>
              </div>
            </div>
          </v-app-bar-title>

          <v-spacer></v-spacer>

          <v-btn
            icon
            variant="text"
            color="white"
            @click="fecharDialogo"
            class="close-btn"
          >
            <v-icon size="24">mdi-close</v-icon>
          </v-btn>
        </v-app-bar>

        <!-- ✅ CONTEÚDO PRINCIPAL COM PADDING TOP PARA HEADER FIXO -->
        <v-card-text class="pa-0 content-with-header">
          <div v-if="maquinaData" class="details-container">
            
            <!-- ✅ SEÇÕES DE INFORMAÇÕES -->
            <div class="sections-container">
              
              <!-- INFORMAÇÕES BÁSICAS -->
              <div class="section-container">
                <div class="section-header">
                  <v-icon color="blue-darken-1" class="mr-2">mdi-information</v-icon>
                  <span class="section-title">Informações Básicas</span>
                </div>
                
                <v-row class="ma-0">
                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="blue-darken-1" class="mr-1">mdi-barcode</v-icon>
                        Número de Série
                      </div>
                      <div class="info-value">{{ maquinaData.numero_serie || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="orange-darken-1" class="mr-1">mdi-cog</v-icon>
                        Tipo de Máquina
                      </div>
                      <div class="info-value">
                        <v-chip color="orange-darken-1" variant="tonal" size="small">
                          {{ maquinaData.tipo_maquina?.tipo_maquinas || 'Não especificado' }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="green-darken-1" class="mr-1">mdi-domain</v-icon>
                        Setor
                      </div>
                      <div class="info-value">
                        <v-chip color="green-darken-1" variant="tonal" size="small">
                          {{ maquinaData.setor?.descricao_setor || 'Não especificado' }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="purple-darken-1" class="mr-1">mdi-domain</v-icon>
                        Sub Setor
                      </div>
                      <div class="info-value">
                        <v-chip color="purple-darken-1" variant="tonal" size="small">
                          {{ maquinaData.sub_setor?.descricao_sub_setor || 'Não especificado' }}
                        </v-chip>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- LOCALIZAÇÃO -->
              <div class="section-container">
                <div class="section-header">
                  <v-icon color="purple-darken-1" class="mr-2">mdi-map-marker</v-icon>
                  <span class="section-title">Localização</span>
                </div>
                
                <v-row class="ma-0">
                  <v-col :cols="getColSize" :md="4" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="purple-darken-1" class="mr-1">mdi-view-column</v-icon>
                        Fila
                      </div>
                      <div class="info-value">{{ maquinaData.fila || '-' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="4" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="purple-darken-1" class="mr-1">mdi-view-grid</v-icon>
                        Posição
                      </div>
                      <div class="info-value">{{ maquinaData.posicao || '-' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="4" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="purple-darken-1" class="mr-1">mdi-map-marker</v-icon>
                        Localização
                      </div>
                      <div class="info-value">{{ maquinaData.localizacao || 'Não especificado' }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- ESPECIFICAÇÕES TÉCNICAS -->
              <div class="section-container">
                <div class="section-header">
                  <v-icon color="indigo-darken-1" class="mr-2">mdi-wrench</v-icon>
                  <span class="section-title">Especificações Técnicas</span>
                </div>
                
                <v-row class="ma-0">
                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="red-darken-1" class="mr-1">mdi-factory</v-icon>
                        Marca
                      </div>
                      <div class="info-value">{{ maquinaData.marca || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="teal-darken-1" class="mr-1">mdi-tag-outline</v-icon>
                        Modelo
                      </div>
                      <div class="info-value">{{ maquinaData.modelo || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="amber-darken-1" class="mr-1">mdi-calendar</v-icon>
                        Data de Aquisição
                      </div>
                      <div class="info-value">{{ formatDate(maquinaData.data_aquisicao) || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="deep-orange-darken-1" class="mr-1">mdi-calendar-check</v-icon>
                        Data de Reposição
                      </div>
                      <div class="info-value">{{ formatDate(maquinaData.data_reposicao) || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="cyan-darken-1" class="mr-1">mdi-shield-check</v-icon>
                        Garantia
                      </div>
                      <div class="info-value">{{ maquinaData.garantia || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="brown-darken-1" class="mr-1">mdi-receipt</v-icon>
                        Nota Fiscal
                      </div>
                      <div class="info-value">{{ maquinaData.nota_fiscal || 'Não informado' }}</div>
                    </div>
                  </v-col>

                  <v-col :cols="getColSize" :md="6" class="info-col">
                    <div class="info-card">
                      <div class="info-label">
                        <v-icon size="16" color="pink-darken-1" class="mr-1">mdi-tag</v-icon>
                        Etiqueta Impressa
                      </div>
                      <div class="info-value">{{ maquinaData.etiqueta_impressa || 'Não informado' }}</div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- OBSERVAÇÕES -->
              <div v-if="maquinaData.observacao" class="section-container">
                <div class="section-header">
                  <v-icon color="grey-darken-1" class="mr-2">mdi-note-text</v-icon>
                  <span class="section-title">Observações</span>
                </div>
                
                <v-card variant="outlined" class="observation-card">
                  <v-card-text class="pa-4">
                    <div class="text-body-1">{{ maquinaData.observacao }}</div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- APARELHOS VINCULADOS -->
              <div v-if="maquinaData.aparelhos?.length" class="section-container">
                <div class="section-header">
                  <v-icon color="cyan-darken-1" class="mr-2">mdi-devices</v-icon>
                  <span class="section-title">Aparelhos Vinculados</span>
                  <v-spacer></v-spacer>
                  <v-chip color="cyan-darken-1" variant="tonal" size="small">
                    {{ maquinaData.aparelhos.length }} aparelho(s)
                  </v-chip>
                </div>
                
                <v-expansion-panels class="devices-panels" variant="accordion">
                  <v-expansion-panel
                    v-for="aparelho in maquinaData.aparelhos"
                    :key="aparelho.id"
                    class="device-panel"
                  >
                    <v-expansion-panel-title class="device-panel-title">
                      <div class="d-flex align-center w-100">
                        <v-avatar size="32" color="cyan-darken-1" variant="tonal" class="mr-3">
                          <v-icon size="16" color="cyan-darken-1">mdi-router-wireless</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="font-weight-medium">
                            Aparelho {{ aparelho.id }} - {{ aparelho.descricao_aparelho }}
                          </div>
                          <div class="text-caption text-grey">
                            {{ aparelho.tipo_aparelho?.descricao_tipo_aparecelho || 'Tipo não especificado' }}
                          </div>
                        </div>
                        <v-chip 
                          :color="aparelho.ativo ? 'success' : 'error'"
                          variant="tonal"
                          size="small"
                          class="mr-2"
                        >
                          {{ aparelho.ativo ? 'Ativo' : 'Inativo' }}
                        </v-chip>
                      </div>
                    </v-expansion-panel-title>
                    
                    <v-expansion-panel-text class="device-panel-content">
                      <v-row class="ma-0">
                        <v-col :cols="getColSize" :md="6" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Data de Cadastro</div>
                            <div class="info-value">{{ formatDate(aparelho.data_cadastro) || 'Não informado' }}</div>
                          </div>
                        </v-col>

                        <v-col :cols="getColSize" :md="6" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Fornecedor</div>
                            <div class="info-value">{{ aparelho.tipo_aparelho?.fornecedor || 'Não informado' }}</div>
                          </div>
                        </v-col>

                        <v-col cols="12" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Observação do Aparelho</div>
                            <div class="info-value">{{ aparelho.observacao || 'Nenhuma observação' }}</div>
                          </div>
                        </v-col>

                        <v-col cols="12" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Observação do Tipo</div>
                            <div class="info-value">{{ aparelho.tipo_aparelho?.observacao || 'Nenhuma observação' }}</div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>

              <!-- CHAMADOS -->
              <div v-if="maquinaData.chamados?.length" class="section-container">
                <div class="section-header">
                  <v-icon color="red-darken-1" class="mr-2">mdi-headset</v-icon>
                  <span class="section-title">Chamados</span>
                  <v-spacer></v-spacer>
                  <v-chip color="red-darken-1" variant="tonal" size="small">
                    {{ maquinaData.chamados.length }} chamado(s)
                  </v-chip>
                </div>
                
                <v-expansion-panels class="tickets-panels" variant="accordion">
                  <v-expansion-panel
                    v-for="chamado in maquinaData.chamados"
                    :key="chamado.id"
                    class="ticket-panel"
                  >
                    <v-expansion-panel-title class="ticket-panel-title">
                      <div class="d-flex align-center w-100">
                        <v-avatar size="32" color="red-darken-1" variant="tonal" class="mr-3">
                          <v-icon size="16" color="red-darken-1">mdi-headset</v-icon>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div class="font-weight-medium">Chamado #{{ chamado.id }}</div>
                          <div class="text-caption text-grey">
                            {{ formatDate(chamado.data_abertura) || 'Data não informada' }}
                          </div>
                        </div>
                        <v-chip 
                          color="warning"
                          variant="tonal"
                          size="small"
                          class="mr-2"
                        >
                          {{ chamado.status_chamado?.descricao_status || 'Status não informado' }}
                        </v-chip>
                      </div>
                    </v-expansion-panel-title>
                    
                    <v-expansion-panel-text class="ticket-panel-content">
                      <v-row class="ma-0">
                        <v-col :cols="getColSize" :md="6" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Data Solicitação Mecânico</div>
                            <div class="info-value">{{ formatDate(chamado.data_solicitacao_mecanico) || 'Não informado' }}</div>
                          </div>
                        </v-col>

                        <v-col :cols="getColSize" :md="6" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Mecânico</div>
                            <div class="info-value">{{ chamado.mecanico?.nome_mecanico || 'Não atribuído' }}</div>
                          </div>
                        </v-col>

                        <v-col :cols="getColSize" :md="6" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Prioridade</div>
                            <div class="info-value">
                              <v-chip 
                                :color="getPriorityColor(chamado.prioridade?.prioridade)"
                                variant="tonal"
                                size="small"
                              >
                                {{ chamado.prioridade?.prioridade || 'Não definida' }}
                              </v-chip>
                            </div>
                          </div>
                        </v-col>

                        <v-col cols="12" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Observação de Abertura</div>
                            <div class="info-value">{{ chamado.observacao_abertura || 'Nenhuma observação' }}</div>
                          </div>
                        </v-col>

                        <v-col cols="12" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Observação do Mecânico</div>
                            <div class="info-value">{{ chamado.observacao_mecanico || 'Nenhuma observação' }}</div>
                          </div>
                        </v-col>

                        <v-col cols="12" class="info-col">
                          <div class="info-card">
                            <div class="info-label">Observação de Encerramento</div>
                            <div class="info-value">{{ chamado.observacao_encerramento || 'Nenhuma observação' }}</div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- ✅ AÇÕES DO RODAPÉ FIXO -->
        <v-footer class="footer-actions" color="white" elevation="8">
          <div class="d-flex align-center justify-end w-100 pa-4">
            <v-btn
              v-if="habilitarSelecao"
              color="primary"
              variant="flat"
              @click="selecionarMaquina"
              :disabled="!maquinaData"
              class="mr-3"
            >
              <v-icon start>mdi-check</v-icon>
              Selecionar Máquina
            </v-btn>
            
            <v-btn
              color="grey-darken-1"
              variant="outlined"
              @click="fecharDialogo"
            >
              <v-icon start>mdi-close</v-icon>
              Fechar
            </v-btn>
          </div>
        </v-footer>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { useDisplay } from "vuetify";
import { maquinaService } from "@/services/maquinaService";

export default {
  name: "ComponenteMaquinaDialog",
  
  props: {
    habilitarSelecao: {
      type: Boolean,
      default: false,
    },
    idMaquina: {
      type: [Number, null],
      required: true,
    },
    ativa: {
      type: Boolean,
      default: false,
    },
  },
  
  emits: ["close", "maquinaSelecionada", "update:ativa"],

  data() {
    return {
      maquinaData: null,
      ativarMaquina: false,
      loadingDialog: false,
    };
  },

  computed: {
    isMobile() {
      const { xs } = useDisplay();
      return xs.value;
    },

    isTablet() {
      const { sm, md } = useDisplay();
      return sm.value || md.value;
    },

    getColSize() {
      if (this.isMobile) return 12;
      return 6;
    },
  },

  watch: {
    ativa(newAtiva) {
      if (newAtiva && this.idMaquina) {
        this.fetchMaquinaData(this.idMaquina);
      } else if (!newAtiva) {
        this.ativarMaquina = false;
      }
    },

    idMaquina(newId) {
      if (newId && this.ativa) {
        this.fetchMaquinaData(newId);
      }
    },

    loadingDialog(value) {
      if (!value && this.maquinaData) {
        this.ativarMaquina = true;
      }
    },
  },

  methods: {
    formatId(id) {
      return id ? String(id).padStart(3, "0") : "000";
    },

    formatDate(date) {
      if (!date) return null;
      try {
        return new Date(date).toLocaleDateString('pt-BR');
      } catch (error) {
        return null;
      }
    },

    getPriorityColor(priority) {
      const priorityMap = {
        'alta': 'error',
        'média': 'warning',
        'baixa': 'success'
      };
      return priorityMap[priority?.toLowerCase()] || 'grey';
    },

    async fetchMaquinaData(id) {
      this.loadingDialog = true;

      try {
        const data = await maquinaService.obterPorId(id);
        this.maquinaData = data;
      } catch (error) {
        console.error("Erro ao carregar detalhes da máquina:", error);
        this.maquinaData = null;
      } finally {
        this.loadingDialog = false;
      }
    },

    fecharDialogo() {
      console.log("Fechando diálogo");
      this.updateAtiva(false);
      this.$emit("close");
      
      // Reset dos dados
      setTimeout(() => {
        this.maquinaData = null;
      }, 300);
    },

    selecionarMaquina() {
      console.log("Selecionando máquina:", this.maquinaData);
      this.$emit("maquinaSelecionada", this.maquinaData);
    },

    updateAtiva(value) {
      this.$emit("update:ativa", value);
    },
  },
};
</script>

<style scoped>
.machine-details-card {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ✅ HEADER FIXO */
.machine-header {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 2000 !important;
  background: linear-gradient(135deg, #1976d2 0%, #2196f3 100%) !important;
}

.machine-title {
  width: 100%;
}

.title-content {
  flex: 1;
  min-width: 0;
}

.machine-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}

.machine-id {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin-top: 2px;
}

.close-btn {
  flex-shrink: 0;
}

/* ✅ CONTEÚDO COM PADDING PARA HEADER FIXO */
.content-with-header {
  padding-top: 80px !important; /* Espaço para o header fixo */
  padding-bottom: 80px !important; /* Espaço para o footer fixo */
  height: 100vh;
  overflow-y: auto;
}

.sections-container {
  min-height: calc(100vh - 160px);
}

.section-container {
  padding: 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.section-container:last-child {
  border-bottom: none;
  padding-bottom: 40px; /* Espaço extra no final */
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

.info-col {
  padding: 8px;
}

.info-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  height: 100%;
  transition: all 0.3s ease;
}

.info-card:hover {
  background: #e9ecef;
  transform: translateY(-2px);
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.info-value {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  word-break: break-word;
}

.observation-card {
  background: #f8f9fa;
  border-radius: 12px;
  margin: 0 24px;
}

.devices-panels,
.tickets-panels {
  margin: 0 24px;
}

.device-panel,
.ticket-panel {
  border-radius: 12px !important;
  margin-bottom: 8px;
}

.device-panel-title,
.ticket-panel-title {
  padding: 16px !important;
}

.device-panel-content,
.ticket-panel-content {
  padding: 0 !important;
}

/* ✅ FOOTER FIXO */
.footer-actions {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 1000 !important;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.loading-card {
  border-radius: 16px;
}

/* ✅ SCROLL CUSTOMIZADO */
.content-with-header::-webkit-scrollbar {
  width: 6px;
}

.content-with-header::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.content-with-header::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #1976d2, #2196f3);
  border-radius: 3px;
}

.content-with-header::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

/* ✅ RESPONSIVIDADE MOBILE */
@media (max-width: 600px) {
  .machine-name {
    font-size: 1.1rem;
  }
  
  .machine-id {
    font-size: 0.8rem;
  }
  
  .section-container {
    padding: 16px;
  }
  
  .observation-card,
  .devices-panels,
  .tickets-panels {
    margin: 0 16px;
  }
  
  .info-card {
    padding: 12px;
  }
  
  .section-title {
    font-size: 1rem;
  }
  
  .content-with-header {
    padding-top: 70px !important;
    padding-bottom: 70px !important;
  }
}

/* ✅ RESPONSIVIDADE TABLET */
@media (min-width: 601px) and (max-width: 960px) {
  .section-container {
    padding: 20px;
  }
  
  .observation-card,
  .devices-panels,
  .tickets-panels {
    margin: 0 20px;
  }
}

/* ✅ ANIMAÇÕES */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.info-card {
  animation: slideInUp 0.3s ease;
}

.info-card:nth-child(1) { animation-delay: 0.1s; }
.info-card:nth-child(2) { animation-delay: 0.2s; }
.info-card:nth-child(3) { animation-delay: 0.3s; }
.info-card:nth-child(4) { animation-delay: 0.4s; }

/* ✅ ESTADOS ESPECIAIS */
.v-expansion-panel--active .device-panel-title,
.v-expansion-panel--active .ticket-panel-title {
  background: rgba(25, 118, 210, 0.05);
}

/* ✅ TRANSIÇÃO DO DIALOG */
.dialog-bottom-transition-enter-active,
.dialog-bottom-transition-leave-active {
  transition: transform 0.3s ease-in-out;
}

.dialog-bottom-transition-enter-from {
  transform: translateY(100%);
}

.dialog-bottom-transition-leave-to {
  transform: translateY(100%);
}
</style>